// ============================================
// Clinical Calibration Types
// Doctor Training Module — Blind Diagnosis Workflow
// MedGemma Comparison Engine
// ============================================

import type { LabValue } from './patientJourney';

/** A single calibration session — Dr. C's blind diagnosis vs MedGemma */
export interface CalibrationSession {
  sessionId: string;
  physicianId: string;
  brandId: string;

  // Case source
  caseSource: 'random' | 'ran_test' | 'stock';
  caseRef: string;  // personaId, patientId, or stockCaseId

  // Phase 1 inputs (what Dr. C submitted)
  flaggedLabs: FlaggedLab[];
  chatNotes: string[];
  selectedProducts: string[];
  selectedLabOrders: string[];
  diagnosisText: string;
  diagnosisAudio?: string;
  inputMode: 'type' | 'voice';

  // Phase 2 comparison (auto-generated)
  medgemmaAssessment: MedGemmaAssessment;
  decisionPoints: DecisionPoint[];
  alignmentScore: number;  // 0-100

  // Phase 3 tuning (Dr. C's instructions)
  tuningInstructions: TuningMessage[];

  // Metadata
  startedAt: string;
  submittedAt?: string;
  completedAt?: string;
  status: 'in_progress' | 'submitted' | 'reviewed' | 'tuning_applied';
}

export interface FlaggedLab {
  marker: string;
  value: string;
  unit: string;
  stdRange: string;
  functionalRange?: string;
  flagReason?: string;
}

export interface MedGemmaAssessment {
  diagnosis: string;
  protocol: string;
  grade: 'A' | 'A-' | 'B+' | 'B' | 'B-' | 'C+' | 'C';
  matchedProtocols: string[];
  recommendedProducts: string[];
  recommendedLabOrders: string[];
  totalMonthlyCost: number;
  escalationPath?: string;
}

export interface DecisionPoint {
  area: string;
  drCPosition: string;
  medgemmaPosition: string;
  alignment: 'aligned' | 'delta' | 'both_missed';
  deltaNote?: string;
  clinicalWeight: number;  // 0-1
}

export interface TuningMessage {
  role: 'physician' | 'system';
  text: string;
  timestamp: string;
  appliedToModel?: boolean;
}

// --- Stock Cases ---

export interface StockCase {
  caseId: string;
  label: string;
  acuity: 'moderate' | 'complex' | 'high';
  labs: LabValue[];
  transcript: TranscriptMessage[];
  tags: TranscriptTag[];
  expectedDiagnosis?: string;
  targetDecisionPoints?: string[];
  createdBy: string;
  version: number;
}

export interface TranscriptMessage {
  role: 'patient' | 'nurse';
  text: string;
  tags: string[];
}

export interface TranscriptTag {
  tag: string;
  messageIndex: number;
  category: 'symptom' | 'history' | 'medication' | 'concern' | 'lifestyle';
}

// --- Cloud Function Request/Response ---

export interface CalibrationCaseRequest {
  brandId: string;
  physicianId: string;
  caseSource: 'random' | 'ran_test' | 'stock';
  caseRef?: string;
}

export interface CalibrationCaseResponse {
  sessionId: string;
  labs: LabValue[];
  transcript: TranscriptMessage[];
  formulary: FormularySubset[];
  labPanels: LabPanelGroup[];
}

/** Simplified formulary product for calibration UI (no pricing shown) */
export interface FormularySubset {
  productId: string;
  name: string;
  contents: string;
  productType: 'supplement' | 'prescription' | 'longevity';
  requiresProvider: boolean;
}

export interface LabPanelGroup {
  groupId: string;
  groupLabel: string;  // 'Thyroid', 'Hormone', 'Metabolic', 'Lipid'
  panels: LabPanelItem[];
}

export interface LabPanelItem {
  panelId: string;
  name: string;
}

// --- Decision Point Areas (9 standard areas) ---

export const DECISION_AREAS: { area: string; clinicalWeight: number }[] = [
  { area: 'Primary Dx', clinicalWeight: 0.20 },
  { area: 'Key Lab Read', clinicalWeight: 0.15 },
  { area: 'Secondary Findings', clinicalWeight: 0.10 },
  { area: 'Supplement-First', clinicalWeight: 0.15 },
  { area: 'Product Selection', clinicalWeight: 0.10 },
  { area: 'Escalation Path', clinicalWeight: 0.10 },
  { area: 'Missed Signals', clinicalWeight: 0.08 },
  { area: 'Cost Awareness', clinicalWeight: 0.07 },
  { area: 'Evidence Grade', clinicalWeight: 0.05 },
];

/** MedGemma evidence grades */
export type EvidenceGrade = 'A' | 'A-' | 'B+' | 'B' | 'B-' | 'C+' | 'C';
